import React, { useCallback, useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import { validCategorySettings } from '../../services/categoryPricing';
import { useT } from '../../i18n/LanguageContext';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import DistributorAddressFields from './DistributorAddressFields';
import DistributorClinicSettings, {
  fromDetail,
  changed,
  matches,
} from './DistributorClinicSettings';

const ADDRESS = { street: '', city: '', state: '', zipCode: '' };
const validDetail = (detail, id) =>
  detail?._id === id && validCategorySettings(detail.settings) && Number.isInteger(detail.revision);

/** Real-admin assistance for one independent organization; never impersonates its master. */
export default function AdminDistributorCustomers({
  distributor,
  onLockChange,
  onSaved,
  t: providedT,
  initiallyExpanded = false,
  initialClinicId,
  detailOnly = false,
  readOnly = false,
}) {
  const translate = useT();
  const t = providedT || translate;
  const id = distributor._id;
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [customers, setCustomers] = useState(null);
  const [products, setProducts] = useState(null);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [newCustomer, setNewCustomer] = useState({ name: '', ...ADDRESS });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [usersBusy, setUsersBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const mounted = useRef(false);
  const sequence = useRef(0);
  const pending = useRef('');
  const session = useRef(captureSession());
  const initialOpened = useRef(false);
  const current = () => mounted.current && isSessionCurrent(session.current);
  const unsaved = Boolean(
    selected && draft && Object.keys(changed(fromDetail(selected), draft)).length
  );
  const creatingDraft = Object.values(newCustomer).some((value) => value.trim());

  const load = useCallback(async () => {
    const captured = session.current;
    if (!mounted.current || !isSessionCurrent(captured)) return false;
    const generation = ++sequence.current;
    setLoading(true);
    try {
      const response = await distributorService.adminCustomers(id);
      if (!mounted.current || !isSessionCurrent(captured) || generation !== sequence.current)
        return;
      const rows = response?.data?.data;
      if (!Array.isArray(rows) || rows.some((row) => !row._id || typeof row.name !== 'string'))
        throw new Error('Invalid customer list');
      setCustomers(rows);
      setUncertain(false);
      return true;
    } catch {
      if (mounted.current && isSessionCurrent(captured) && generation === sequence.current) {
        setCustomers(null);
        setError('Could not load distributor customers. Please retry.');
      }
      return false;
    } finally {
      if (mounted.current && isSessionCurrent(captured) && generation === sequence.current)
        setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!expanded || (distributor.isActive === false && !detailOnly)) return;
    mounted.current = true;
    session.current = captureSession();
    load();
    return () => {
      mounted.current = false;
      sequence.current += 1;
    };
  }, [load, expanded, distributor.isActive, detailOnly]);
  useEffect(() => {
    onLockChange(busy || usersBusy || unsaved || creatingDraft);
    const warn = (event) => {
      if (busy || usersBusy || unsaved || creatingDraft) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      onLockChange(false);
      window.removeEventListener('beforeunload', warn);
    };
  }, [busy, usersBusy, unsaved, creatingDraft, onLockChange]);

  const open = async (clinicId) => {
    if (!current()) return;
    if (pending.current || usersBusy || unsaved || creatingDraft) {
      setError('Save or discard your current edits before opening another clinic.');
      return;
    }
    const generation = ++sequence.current;
    setError('');
    setNotice('');
    setSelected(null);
    setDraft(null);
    setProducts(null);
    try {
      const [customer, options] = await Promise.all([
        distributorService.adminCustomer(id, clinicId),
        distributorService.adminProductOptions(id).catch(() => null),
      ]);
      const detail = customer?.data?.data;
      if (!current() || generation !== sequence.current) return;
      if (!validDetail(detail, clinicId)) throw new Error('Invalid customer');
      setProducts(Array.isArray(options?.data?.data) ? options.data.data : null);
      setSelected(detail);
      setDraft(fromDetail(detail));
    } catch {
      if (current() && generation === sequence.current)
        setError('Could not open this clinic. Please try again.');
    }
  };
  useEffect(() => {
    if (initialClinicId && customers && !initialOpened.current) {
      initialOpened.current = true;
      open(initialClinicId);
    }
  });
  const create = async (event) => {
    event.preventDefault();
    if (!current()) return;
    if (pending.current || loading || !customers || uncertain || usersBusy || unsaved) return;
    const name = newCustomer.name.trim();
    const address = Object.fromEntries(
      Object.keys(ADDRESS).map((key) => [key, newCustomer[key].trim()])
    );
    const hasAddress = Object.values(address).some(Boolean);
    if (
      name.length < 2 ||
      (hasAddress &&
        (!address.street ||
          !address.city ||
          !/^[A-Z]{2}$/.test(address.state) ||
          !/^\d{5}(?:-\d{4})?$/.test(address.zipCode)))
    ) {
      setError('Enter a clinic name and a complete delivery address, or leave the address blank.');
      return;
    }
    pending.current = 'create';
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const receipt = (
        await distributorService.adminCreateCustomer(id, {
          name,
          ...(hasAddress ? { deliveryAddress: address } : {}),
        })
      )?.data?.data;
      if (!current()) return;
      if (!receipt?._id) throw new Error('Unconfirmed customer');
      setNewCustomer({ name: '', ...ADDRESS });
      setNotice('Clinic created.');
      await load();
      if (current()) {
        try {
          await onSaved?.();
        } catch {
          if (current())
            setError(
              'Customer saved, but setup could not refresh. Refresh setup before continuing.'
            );
        }
      }
    } catch {
      if (!current()) return;
      const refreshed = await load();
      if (!current()) return;
      setUncertain(!refreshed);
      if (refreshed) {
        try {
          await onSaved?.();
        } catch {
          /* The safe uncertain notice remains authoritative. */
        }
      }
      if (!current()) return;
      setError(
        'Clinic creation could not be confirmed. Check the clinic list before trying again.'
      );
    } finally {
      pending.current = '';
      if (current()) setBusy(false);
    }
  };
  const save = async (event) => {
    event.preventDefault();
    if (!current()) return;
    if (pending.current || usersBusy || !selected || !draft || uncertain) return;
    const changes = changed(fromDetail(selected), draft);
    if (!Object.keys(changes).length) return;
    if (
      ['ratePerUnit', 'creditLimit'].some(
        (key) => changes[key] != null && (!Number.isFinite(changes[key]) || changes[key] < 0)
      )
    ) {
      setError('Enter valid nonnegative amounts.');
      return;
    }
    if ('categoryRates' in changes && changes.categoryRates === null) {
      setError('Enter a retail price per piece from $0 to $1,000,000 with at most two decimals.');
      return;
    }
    const address = changes.deliveryAddress;
    if (
      address &&
      (!address.street ||
        !address.city ||
        !/^[A-Z]{2}$/.test(address.state) ||
        !/^\d{5}(?:-\d{4})?$/.test(address.zipCode))
    ) {
      setError('Enter a complete delivery address or clear all its fields.');
      return;
    }
    const clinicId = selected._id;
    pending.current = 'save';
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const detail = (
        await distributorService.adminSaveCustomer(id, clinicId, {
          revision: selected.revision,
          changes,
        })
      )?.data?.data;
      if (!current()) return;
      if (
        !validDetail(detail, clinicId) ||
        detail.revision !== selected.revision + 1 ||
        !matches(detail, changes)
      )
        throw new Error('Unconfirmed save');
      setSelected(detail);
      setDraft(fromDetail(detail));
      setNotice('Settings saved.');
      try {
        await onSaved?.();
      } catch {
        if (current())
          setError('Customer saved, but setup could not refresh. Refresh setup before continuing.');
      }
    } catch {
      if (!current()) return;
      try {
        const detail = (await distributorService.adminCustomer(id, clinicId))?.data?.data;
        if (!current()) return;
        if (!validDetail(detail, clinicId)) throw new Error('Invalid customer');
        setSelected(detail);
        if (matches(detail, changes)) {
          setDraft(fromDetail(detail));
          setNotice('Settings saved.');
          try {
            await onSaved?.();
          } catch {
            if (current())
              setError(
                'Customer saved, but setup could not refresh. Refresh setup before continuing.'
              );
          }
        } else {
          setUncertain(true);
          setError(
            'Settings changed or could not be confirmed. Review the latest saved values before trying again. Your edits are still here.'
          );
        }
      } catch {
        if (current()) {
          setUncertain(true);
          setError(
            'Settings could not be confirmed. Your edits are still here. Reload the clinic before trying again.'
          );
        }
      }
    } finally {
      pending.current = '';
      if (current()) setBusy(false);
    }
  };
  const discard = async () => {
    if (!current()) return;
    if (pending.current || usersBusy) return;
    if (!uncertain) {
      setDraft(fromDetail(selected));
      setError('');
      return;
    }
    const clinicId = selected._id;
    const generation = ++sequence.current;
    pending.current = 'reload';
    setBusy(true);
    setError('');
    try {
      const [customer, options] = await Promise.all([
        distributorService.adminCustomer(id, clinicId),
        distributorService.adminProductOptions(id).catch(() => null),
      ]);
      const detail = customer?.data?.data;
      if (!current() || generation !== sequence.current) return;
      if (!validDetail(detail, clinicId)) throw new Error('Invalid customer');
      setProducts(Array.isArray(options?.data?.data) ? options.data.data : null);
      setSelected(detail);
      setDraft(fromDetail(detail));
      setUncertain(false);
    } catch {
      if (current() && generation === sequence.current)
        setError('Could not open this clinic. Please try again.');
    } finally {
      pending.current = '';
      if (current() && generation === sequence.current) setBusy(false);
    }
  };
  const inputClass = 'block w-full rounded-lg border px-3 py-2';
  const visible = customers?.filter((row) =>
    row.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
  );
  const description = (
    <p className="text-sm text-slate-600">
      {t(
        "This customer list is separate from direct MedEffects clinics. Active master logins share this distributor's customers and product bank. Clinic logins see only their own distributor clinic records and cannot access the product bank."
      )}
    </p>
  );
  if (!expanded || (distributor.isActive === false && !detailOnly))
    return (
      <div className="space-y-3">
        {description}
        {distributor.isActive !== false && (
          <button
            type="button"
            className="me-distributor-admin-primary"
            onClick={() => setExpanded(true)}
          >
            {t('Manage distributor customers')}
          </button>
        )}
      </div>
    );
  return (
    <section
      aria-label={t('Admin customer assistance')}
      className="me-distributor-admin-card space-y-5"
    >
      {!detailOnly && description}
      {!detailOnly && (
        <h2 className="text-lg font-semibold">
          {t('Manage customers for {name}', { name: distributor.name })}
        </h2>
      )}
      {!detailOnly && (
        <p className="text-sm text-slate-600">
          {t(
            'Admin changes apply only to this distributor. Direct MedEffects clinics and other distributors are not changed.'
          )}
        </p>
      )}
      {error && (
        <p role="alert" className="text-red-700">
          {t(error)}
        </p>
      )}
      {notice && (
        <p role="status" className="text-green-700">
          {t(notice)}
        </p>
      )}
      {loading && <p role="status">{t('Loading clinics...')}</p>}
      {!loading && !customers && (
        <button
          type="button"
          onClick={() => {
            setError('');
            load();
          }}
          className="me-distributor-admin-secondary"
        >
          {t('Retry')}
        </button>
      )}
      {detailOnly && !selected && !loading && (
        <button type="button" className="underline" onClick={() => open(initialClinicId)}>
          {t('Reload clinic')}
        </button>
      )}
      {!detailOnly && (
        <form onSubmit={create} className="space-y-3">
          <fieldset
            disabled={
              readOnly || busy || loading || !customers || uncertain || usersBusy || unsaved
            }
            className="space-y-3"
          >
            <label className="block">
              {t('Customer name')}
              <input
                required
                maxLength={120}
                className={inputClass}
                value={newCustomer.name}
                onChange={(event) =>
                  setNewCustomer((value) => ({ ...value, name: event.target.value }))
                }
              />
            </label>
            <DistributorAddressFields
              address={newCustomer}
              onChange={(key, value) => setNewCustomer((current) => ({ ...current, [key]: value }))}
              t={t}
              inputClass={inputClass}
            />
            <button className="me-distributor-admin-primary" type="submit">
              {t(pending.current === 'create' ? 'Creating...' : 'Create customer')}
            </button>
          </fieldset>
          {creatingDraft && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setNewCustomer({ name: '', ...ADDRESS })}
              className="me-distributor-admin-text-button"
            >
              {t('Discard changes')}
            </button>
          )}
        </form>
      )}
      {!detailOnly && customers && (
        <>
          <label className="block">
            {t('Search customers')}
            <input
              type="search"
              className={inputClass}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <p className="text-sm">
            {t(customers.length === 1 ? '{count} customer' : '{count} customers', {
              count: customers.length,
            })}
          </p>
          {!visible.length && (
            <p>
              {t(
                customers.length
                  ? 'No customers match your search.'
                  : 'No distributor customers yet. Create a customer above to get started.'
              )}
            </p>
          )}
          <ul className="divide-y">
            {visible.map((row) => (
              <li key={row._id} className="flex justify-between gap-3 py-3">
                <span>{row.name}</span>
                <button
                  type="button"
                  className="me-distributor-admin-text-button"
                  disabled={busy || usersBusy}
                  onClick={() => open(row._id)}
                >
                  {t('View')}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {selected && draft && (
        <div className="space-y-3">
          {!detailOnly && <h3 className="font-semibold">{selected.name}</h3>}
          {uncertain && (
            <p className="text-sm">
              {t('Discard changes to reload the latest saved settings before saving again.')}
              <button
                type="button"
                disabled={busy || usersBusy}
                onClick={discard}
                className="ml-2 underline"
              >
                {t('Discard changes')}
              </button>
            </p>
          )}
          <DistributorClinicSettings
            selected={selected}
            draft={draft}
            products={products}
            saving={busy || usersBusy}
            isSavingSettings={busy}
            readOnly={uncertain || readOnly}
            usersReadOnly={busy || unsaved || creatingDraft || uncertain || readOnly}
            unsaved={unsaved}
            inputClass={inputClass}
            setField={(key, value) => setDraft((current) => ({ ...current, [key]: value }))}
            setAddress={(key, value) =>
              setDraft((current) => ({
                ...current,
                deliveryAddress: { ...current.deliveryAddress, [key]: value },
              }))
            }
            onSave={save}
            onDiscard={discard}
            distributorId={id}
            onUsersBusyChange={setUsersBusy}
            t={t}
          />
        </div>
      )}
    </section>
  );
}
